import fetchClient, { ApiResponse } from '../api-client'
import { CheckLocationResponse } from './types/CheckLocationResponse'
import { CreatePackagePayload } from './types/CreatePackagePayload'
import { GetAllPackagesFilter, GetAllPackagesResponse } from './types/GetAllPackagesFilter'
import { GetPackagesByIndexPayload } from './types/GetPackagesByIndexPayload'
import { GetPackagesFilter } from './types/GetPackagesFilter'
import { PackageStatsResponse } from './types/PackageStats'
import { Pkg } from './types/Pkg'
import { UpdatePackagePayload } from './types/UpdatePackagePayload'

/**
 * Get all packages with optional filtering
 */
export const getAllPackages = async (filter?: GetAllPackagesFilter): Promise<GetAllPackagesResponse> => {
  try {
    const res = await fetchClient.post<ApiResponse<{ response: GetAllPackagesResponse }>>(`/packages/all`, filter)
    return res.data.data?.response || { items: [], nextPage: undefined }
  } catch (error) {
    throw error
  }
}

/**
 * Get packages statistics
 */
export const getPackagesStats = async (filter?: GetAllPackagesFilter): Promise<PackageStatsResponse> => {
  try {
    const res = await fetchClient.post<ApiResponse<PackageStatsResponse>>(`/packages/stats`, filter)
    return (
      res.data.data || {
        totals: { totalPackages: 0, totalShipped: 0 },
        chartDataMonthP: [],
        chartDataDayP: [],
        chartDataDayW: [],
        brandsArr: [],
        workers: [],
      }
    )
  } catch (error) {
    throw error
  }
}

/**
 * Create a new package
 */
export const createPackage = async (data: CreatePackagePayload): Promise<unknown> => {
  try {
    const payload = {
      data,
    }

    const res = await fetchClient.post<ApiResponse<unknown>>(`/packages`, payload)
    return res.data.data
  } catch (err) {
    throw err
  }
}

/**
 * Get packages with filter
 */
export const getPackages = async (filter: GetPackagesFilter): Promise<Pkg[]> => {
  try {
    const res = await fetchClient.post<ApiResponse<{ response: Pkg[] }>>('/packages/filter', filter)
    return res.data.data?.response || []
  } catch (error) {
    throw error
  }
}

/**
 * Get package by ID
 */
export const getPackageById = async (id: string): Promise<Pkg> => {
  try {
    const result = await fetchClient.get<ApiResponse<{ response: Pkg[] }>>(`/packages/${id}`)
    if (!result.data.data?.response || result.data.data.response.length === 0) {
      throw new Error('Package not found!')
    }

    return result.data.data.response[0]
  } catch (error) {
    throw error
  }
}

/**
 * Get package by tracking number in Turkey
 */
export const getPackageByTrackingNumberInTurkey = async (trackingNumber: string): Promise<Pkg[]> => {
  try {
    const result = await fetchClient.get<ApiResponse<{ response: Pkg[] }>>(`/packages/trackingNumberInTurkey/${trackingNumber}`)
    return result.data.data?.response || []
  } catch (error) {
    throw error
  }
}

/**
 * Get package by XLT (tracking number to Israel)
 */
export const getPackageByXlt = async (xlt: string): Promise<Pkg | undefined> => {
  try {
    const result = await fetchClient.get<ApiResponse<{ response: Pkg[] }>>(`/packages/xlt/${xlt}`)
    const pkgs = result.data.data?.response || []
    return pkgs.filter(p => !p.unitedPackageId)?.[0]
  } catch (error) {
    throw error
  }
}

/**
 * Get packages by index (e.g., by customer name, email, etc.)
 */
export const getPackagesByIndex = async (payload: GetPackagesByIndexPayload): Promise<Pkg[]> => {
  try {
    const res = await fetchClient.post<ApiResponse<{ response: Pkg[] }>>(`/packages/by_index`, payload)
    return res.data.data?.response || []
  } catch (error) {
    throw error
  }
}

/**
 * Update a package
 */
export const updatePackage = async (id: string, body: UpdatePackagePayload): Promise<unknown> => {
  try {
    const res = await fetchClient.patch<ApiResponse<Pkg>>(`/packages/${id}`, body)
    return res.data
  } catch (error) {
    throw error
  }
}

/**
 * Delete a package
 */
export const deletePackage = async (id: string): Promise<unknown> => {
  try {
    const result = await fetchClient.delete<ApiResponse<unknown>>(`/packages/${id}`)
    return result.data
  } catch (error) {
    throw error
  }
}

/**
 * Generate warehouse report
 */
export const generateWarehouseReport = async (warehouse: string, month: string): Promise<Blob> => {
  try {
    const response = await fetchClient.post<Blob>(
      '/packages/warehouse-report',
      { warehouse, month },
      {
        responseType: 'blob',
      }
    )
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Check location requirements for a package
 */

export const checkLocationRequirements = async (xlt: string, shouldCheckRelated?: boolean) => {
  try {
    const result = await fetchClient.get<ApiResponse<CheckLocationResponse>>(`/packages/location/${xlt}`, {
      params: {
        shouldCheckRelated,
      },
    })
    return result.data.data
  } catch (error) {
    throw error
  }
}
